// Render a contract's plain-text-with-markdown into proper styled
// HTML for the on-screen preview. Mirrors the rules used by the
// PDF letterhead (lib/printContract.js) so the user sees a true
// preview of what they'll print.

import React from "react";

const inlineMd = (s) => {
  // Convert **bold** / *italic* in an already-rendered React text node.
  // We return an array of mixed strings and React elements.
  const parts = [];
  let i = 0;
  const re = /\*\*(.+?)\*\*|(?<!\*)\*(?!\*)([^*]+?)(?<!\*)\*(?!\*)/g;
  let m;
  let cursor = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > cursor) parts.push(s.slice(cursor, m.index));
    if (m[1]) parts.push(<strong key={i++}>{m[1]}</strong>);
    else if (m[2]) parts.push(<em key={i++}>{m[2]}</em>);
    cursor = re.lastIndex;
  }
  if (cursor < s.length) parts.push(s.slice(cursor));
  return parts;
};

const looksLikeClauseHead = (line) => {
  if (line.length > 100) return false;
  return (
    /^\s*\d+\s*[.\-)]\s*/.test(line) ||
    /^\s*(?:أولاً|ثانياً|ثالثاً|رابعاً|خامساً|سادساً|سابعاً|ثامناً|تاسعاً|عاشراً|أولا|ثانيا|ثالثا|رابعا|خامسا|سادسا|سابعا|ثامنا|تاسعا|عاشرا)\s*[:\-،]/.test(line) ||
    /^\s*(?:البند|المادة|الفقرة|القسم|الفصل)\s+(?:الأول|الثاني|الثالث|الرابع|الخامس|السادس|السابع|الثامن|التاسع|العاشر|الحادي عشر|الثاني عشر|الأولى|الثانية|الثالثة|الرابعة|الخامسة|السادسة|السابعة|الثامنة|التاسعة|العاشرة)/.test(line) ||
    /^\s*(?:Article|Clause|Section|Part)\s+(?:[IVX]+|\d+)\b/i.test(line)
  );
};

export default function ContractRender({ content, isAr }) {
  const lines = (content || "").split("\n");
  const out = [];
  let titleRendered = false;
  let lastWasHeading = false;
  let key = 0;

  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, "");
    const trimmed = line.trim();
    if (trimmed === "") {
      if (out.length === 0) return;
      // single small spacer
      out.push(<div key={key++} className="h-2" />);
      lastWasHeading = false;
      return;
    }

    const h1 = /^\s*#\s+(.+?)\s*$/.exec(line);
    if (h1 && !titleRendered) {
      titleRendered = true;
      out.push(
        <h1
          key={key++}
          className="font-display text-2xl lg:text-3xl text-center font-bold tracking-tight py-3 my-4 border-t-2 border-b-2 border-amber-700"
        >
          {inlineMd(h1[1])}
        </h1>
      );
      lastWasHeading = true;
      return;
    }
    const h2 = /^\s*##\s+(.+?)\s*$/.exec(line);
    if (h2) {
      out.push(
        <h2
          key={key++}
          className="font-display text-base lg:text-lg font-bold mt-5 mb-2 pb-1 border-b border-amber-600/60"
        >
          {inlineMd(h2[1])}
        </h2>
      );
      lastWasHeading = true;
      return;
    }
    const h3 = /^\s*###\s+(.+?)\s*$/.exec(line);
    if (h3) {
      out.push(
        <h3 key={key++} className="font-display text-sm font-semibold mt-3 mb-1.5">
          {inlineMd(h3[1])}
        </h3>
      );
      lastWasHeading = true;
      return;
    }
    const allBold = /^\s*\*\*([^*]+?)\*\*\s*:?\s*$/.exec(line);
    if (allBold) {
      out.push(
        <p
          key={key++}
          className={`mt-3 mb-1 font-semibold text-[14px] lg:text-[15px] ${
            isAr ? "border-r-2 pr-3" : "border-l-2 pl-3"
          } border-amber-600/80`}
        >
          {inlineMd(allBold[1])}
        </p>
      );
      lastWasHeading = true;
      return;
    }
    if (looksLikeClauseHead(trimmed) && !lastWasHeading) {
      out.push(
        <p
          key={key++}
          className={`mt-3 mb-1 font-semibold text-[14px] ${
            isAr ? "border-r-2 pr-3" : "border-l-2 pl-3"
          } border-amber-600/80`}
        >
          {inlineMd(trimmed)}
        </p>
      );
      lastWasHeading = true;
      return;
    }
    if (/^\s*[-_]{3,}\s*$/.test(line)) {
      out.push(
        <div key={key++} className="mt-6 mb-2 w-3/5 border-t border-slate-900" />
      );
      lastWasHeading = false;
      return;
    }
    if (!titleRendered) {
      titleRendered = true;
      out.push(
        <h1
          key={key++}
          className="font-display text-2xl lg:text-3xl text-center font-bold tracking-tight py-3 my-4 border-t-2 border-b-2 border-amber-700"
        >
          {inlineMd(trimmed)}
        </h1>
      );
      lastWasHeading = true;
      return;
    }
    out.push(
      <p
        key={key++}
        className={`text-[13px] leading-[1.75] mb-1.5 text-slate-800 text-justify`}
      >
        {inlineMd(line)}
      </p>
    );
    lastWasHeading = false;
  });

  return (
    <div
      data-testid="contract-output"
      dir={isAr ? "rtl" : "ltr"}
      className={`${isAr ? "font-arabic" : "font-body"} text-slate-800`}
    >
      {out}
    </div>
  );
}
